'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowUpRight, FiClock } from 'react-icons/fi';
import Link from 'next/link';
import CareerItem from '@/components/Careers/CareerItem';
import { useLanguageStore } from '@/providers/useLanguageStore';
import OnDemandItem from '../Courses/OndemandItem';
import { COURSES_TYPES } from '@/constants/courses';
import SkeletonCard from '../SkeletonCard';
import CourseItem from '../Courses/CourseItem';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { getCoursePageTitle } from '@/utils/getCoursePageTitle';
import CareerFilters, {
  CareerFilterKey,
  CAREER_FILTERS,
  filterCareersByKey,
} from '@/components/Careers/CareerFilters';

interface Schedule {
  day: string;
  since: string;
  to: string;
}

interface CareerData {
  _id: string;
  course: string;
  classroom: string;
  schedule: Schedule[];
  initialDate: string;
  coverThumbnail: string;
  slackChannel?: string;
  plan: string;
  mode?: string;
  officeLocation?: string;
  careerData?: {
    name: string;
    description?: string;
    durationMonths?: number;
  };
}
interface CourseData {
  _id: string;
  course: string;
  classroom: string;
  coverThumbnail: string;
  schedule: Schedule[];
  initialDate: string;
  slackChannel?: string;
  plan: string;
  mode?: string;
}

interface CourseOnDemand {
  _id: string;
  name: string;
  description: string;
  coverThumbnail: string;
  coursePrice: number;
  published: boolean;
  ranking?: number;
  categoryId?: string;
  subCategoryId?: string;
  monetization?: {
    price: number;
    finalPrice: number;
    discount: number;
  };
}

const CAREER_FILTER_PARAM = 'careerFilter';

const CareersList: React.FC<{ type: string; prefix: string }> = ({
  type,
  prefix,
}) => {
  const [careers, setCareers] = useState<CareerData[]>([]);
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [onDemandCourses, setOnDemandCourses] = useState<CourseOnDemand[]>([]);
  const [loading, setLoading] = useState(true);
  const { translations } = useLanguageStore();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const courseNameParam = searchParams.get('courseName');
  const careerFilterParam = (searchParams.get(CAREER_FILTER_PARAM) || '').toLowerCase() as
    | CareerFilterKey
    | '';

  const handleCareerFilterChange = (filterKey: CareerFilterKey) => {
    const params = new URLSearchParams(searchParams.toString());
    if (filterKey === CAREER_FILTERS.todos.key || careerFilterParam === filterKey) {
      params.delete(CAREER_FILTER_PARAM);
    } else {
      params.set(CAREER_FILTER_PARAM, filterKey);
    }

    const query = params.toString();
    router.push(query ? `${pathname}?${query}` : pathname, { scroll: false });
  };

  const tabs = [
    {
      label: `${translations.careers || 'Carreras'} `,
      path: `/${prefix}/fullstack`,
      disabled: false,
    },
    {
      label: `${translations.liveCourses || 'Cursos en vivo'} `,
      path: `/${prefix}/live-courses`,
      disabled: false,
    },
    {
      label: `${translations.ondemand || 'Cursos Flexibles'} `,
      path: `/${prefix}/ondemand`,
      disabled: false,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (type === COURSES_TYPES.FULLSTACK) {
          const response = await fetch(`/api/careers/${type}`);
          const data = await response.json();
          setCareers(data);
        } else if (type === COURSES_TYPES.LIVE_COURSES) {
          let url = '/api/courses?sortedOrder=desc';
          if (courseNameParam) {
            url = `/api/courses?courseName=${encodeURIComponent(
              courseNameParam
            )}&sortedOrder=desc`;
          }
          const response = await fetch(url);
          const data = await response.json();
          setCourses(data);
        } else if (type === COURSES_TYPES.ONDEMAND) {
          const response = await fetch(`/api/ondemand`);
          const data = await response.json();
          setOnDemandCourses(data);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [type, courseNameParam]);

  const filteredCareers = useMemo(() => {
    if (type !== COURSES_TYPES.FULLSTACK) {
      return careers;
    }

    return filterCareersByKey(careers, careerFilterParam);
  }, [careers, careerFilterParam, type]);

  const title = getCoursePageTitle(type, translations);

  return (
    <div className="container mx-auto py-4">
      <h2 className="text-4xl lg:text-4xl font-bold text-red-600 font-archivo mb-4 md:mb-12 flex items-center justify-center w-full text-center">
        <FiArrowUpRight className="text-red-600 text-4xl mr-2" />
        {title}
      </h2>

      <div className="flex justify-center px-4 mb-8">
        <div className="w-full max-w-2xl bg-gray-100 p-1.5 rounded-2xl sm:rounded-full border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-1 sm:gap-1.5">
          {tabs.map((tab) => {
            const isActive = tab.path === `/${prefix}/${type}`;
            return (
              <Link
                key={tab.label}
                href={tab.path}
                className={`flex-1 py-2.5 px-4 text-center flex justify-center items-center rounded-xl sm:rounded-full text-sm sm:text-base font-semibold transition-all duration-200 ${
                  tab.disabled
                    ? 'bg-transparent text-gray-400 cursor-not-allowed opacity-50'
                    : isActive
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'text-gray-700 hover:text-red-600 hover:bg-gray-200/60'
                }`}
                onClick={(e) => tab.disabled && e.preventDefault()}
              >
                {tab.label.trim()}
              </Link>
            );
          })}
        </div>
      </div>

      {type === COURSES_TYPES.FULLSTACK && (
        <CareerFilters
          className="mb-8"
          activeFilter={careerFilterParam}
          onFilterChange={handleCareerFilterChange}
        />
      )}
      {[
        {
          condition: type === COURSES_TYPES.FULLSTACK,
          text: translations.carrersDescription,
        },
        {
          condition: type === COURSES_TYPES.LIVE_COURSES,
          text: translations.liveCoursesDescription,
        },
        {
          condition: type === COURSES_TYPES.ONDEMAND,
          text: translations.onDemandCoursesDescription,
        },
      ]
        .filter(({ condition }) => condition)
        .map(({ text }, idx) => (
          <p key={idx} className="text-center text-gray-500 my-4 text-lg">
            {text}
          </p>
        ))}
      {type === COURSES_TYPES.FULLSTACK && filteredCareers.length > 0 && (
        <h3 className="mb-5 text-center text-2xl font-bold tracking-[0.08em] text-red-600 uppercase md:text-3xl">
          HORARIOS DISPONIBLES
        </h3>
      )}
      <div className="flex flex-wrap justify-center gap-8 items-stretch">
        {loading ? (
          <SkeletonCard></SkeletonCard>
        ) : type === COURSES_TYPES.FULLSTACK ? (
          filteredCareers.length > 0 ? (
            filteredCareers.map((career) => (
              <CareerItem key={career._id} career={career} />
            ))
          ) : (
            <p className="text-center text-gray-600 w-full">
              {careerFilterParam
                ? 'No hay carreras disponibles para este filtro'
                : translations.careersNotFound || 'No hay cursos disponibles'}
            </p>
          )
        ) : type === COURSES_TYPES.ONDEMAND ? (
          onDemandCourses.length > 0 ? (
            onDemandCourses.map((course) => (
              <OnDemandItem key={course._id} course={course} />
            ))
          ) : (
            <p className="text-center text-gray-600 w-full">
              {translations.coursesOndemandNotFound ||
                'No hay cursos OnDemand disponibles'}
            </p>
          )
        ) : type === COURSES_TYPES.LIVE_COURSES ? (
          courses.length > 0 ? (
            courses.map((course) => (
              <CourseItem key={course._id} course={course} />
            ))
          ) : (
            <p className="text-center text-gray-600 w-full">
              {translations.liveCoursesNotFound ||
                'No hay cursos en vivo disponibles'}
            </p>
          )
        ) : (
          <div className="text-center text-gray-700 w-full">
            <span className="block mb-4 text-2xl font-semibold text-red-600">
              {translations.generalNotFound || 'No hay datos disponibles'}
            </span>
            <span className="block text-lg text-gray-500 mb-6">
              {translations.suggestions ||
                '¡Pero no te preocupes! Pronto habrá más novedades por aquí.'}
            </span>
            <div className="mt-6">
              <FiClock className="mx-auto text-red-500 text-4xl" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareersList;
